/**
 * THE PROCESSING FEE: collected at signing, or financed. Never both.
 *
 * ---------------------------------------------------------------------------
 * THE BROWSER TWIN OF server/lib/fees.js
 * ---------------------------------------------------------------------------
 * Function for function, rule for rule. Read that file's header for why this
 * policy exists at all; this one records why there are two copies.
 *
 * The property page quotes a monthly payment and an amount due today BEFORE any
 * server round trip -- the buyer is moving a term slider, not posting a form.
 * So the rule has to exist in the browser. The alternative, quoting from a
 * server call per keystroke, is not what this page does and would not make the
 * two agree anyway; it would just move the disagreement.
 *
 * What makes the duplication safe is that it is CHECKED. scripts/check-fees.js
 * evaluates this file and its server twin against one fixture table and asserts
 * they return identical figures, so an edit to one that is not made to the
 * other fails a gate. That is the same arrangement monthlyPayment.js has with
 * lib/escrow.js.
 *
 * ---------------------------------------------------------------------------
 * THE RULE
 * ---------------------------------------------------------------------------
 *   upfront    amountFinanced = price - down        the fee is NOT in principal
 *              dueAtSigning   = down + fee          the buyer pays it at the card form
 *
 *   financed   amountFinanced = price - down + fee  the fee IS amortized
 *              dueAtSigning   = down                the checkout must not list it
 *
 * ---------------------------------------------------------------------------
 * THE SERVER STILL DECIDES
 * ---------------------------------------------------------------------------
 * Nothing here is sent to the purchase endpoint. The checkout posts the tier
 * selection and the term and nothing else; calculateFinancing() on the server
 * recomputes every figure from the tenant's own settings and the property's own
 * fee_handling, and that is what the card is charged and what the loan records.
 *
 * This file exists so the page SHOWS the same answer, not so it supplies it.
 */

/** Integer cents from a money value that may be a string, null, or absent. */
const cents = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

/** Integer cents back to dollars. */
const dollars = (c) => Math.round(c) / 100;

/**
 * The two legal values. Migration 057 spells the identical pair into a CHECK
 * constraint on properties.fee_handling and loans.fee_handling.
 */
export const UPFRONT = 'upfront';
export const FINANCED = 'financed';
export const FEE_HANDLING_VALUES = [UPFRONT, FINANCED];

/**
 * A stored fee_handling value, normalised to something safe to branch on.
 *
 * Defaults to 'upfront' for null, undefined and anything unrecognised. That is
 * the safe direction: a wrong guess leaves the buyer paying the fee once at
 * signing rather than once at signing and again with interest.
 *
 * @param {string|null|undefined} value
 * @returns {'upfront'|'financed'}
 */
export const normalizeFeeHandling = (value) => (value === FINANCED ? FINANCED : UPFRONT);

/**
 * Is the fee amortized into principal on this policy?
 * @param {string|null|undefined} feeHandling
 * @returns {boolean}
 */
export const isFeeFinanced = (feeHandling) => normalizeFeeHandling(feeHandling) === FINANCED;

/**
 * THE ONE PLACE THE FEE IS PLACED.
 *
 * Returns both figures at once for the reason the server twin does: a caller
 * that could ask for the amount financed without also being handed the amount
 * due at signing is a caller that can place the fee twice.
 *
 * @param {Object} input
 * @param {number|string} input.price
 * @param {number|string} input.downPayment
 * @param {number|string} input.processingFee
 * @param {string|null}   input.feeHandling
 * @returns {{feeHandling: string, processingFee: number, downPayment: number,
 *           amountFinanced: number, dueAtSigning: number,
 *           feeFinanced: boolean, feeDueAtSigning: boolean}}
 */
export const feeBreakdown = ({ price, downPayment, processingFee, feeHandling } = {}) => {
  const handling = normalizeFeeHandling(feeHandling);

  const priceCents = cents(price);
  const downCents = cents(downPayment);
  // Clamped at zero: a stray minus sign must not become a discount on principal.
  const feeCents = Math.max(0, cents(processingFee));

  const financedCents = priceCents - downCents;

  // ONE branch, and each side places the fee exactly once. Do not refactor this
  // into two independent expressions -- the single conditional is what makes
  // "never both" a property of the code rather than a convention.
  const amountFinancedCents = handling === FINANCED
    ? financedCents + feeCents
    : financedCents;
  const dueAtSigningCents = handling === FINANCED
    ? downCents
    : downCents + feeCents;

  return {
    feeHandling: handling,
    processingFee: dollars(feeCents),
    downPayment: dollars(downCents),
    amountFinanced: dollars(amountFinancedCents),
    dueAtSigning: dollars(dueAtSigningCents),
    feeFinanced: handling === FINANCED,
    feeDueAtSigning: handling !== FINANCED
  };
};

export default feeBreakdown;
